import { useState } from 'react';
import type { Order } from '@/services/orders';

interface LegalReviewSectionProps {
  readonly order: Order;
  readonly onSave: (data: Record<string, unknown>) => void;
  readonly isPending: boolean;
}

export const LegalReviewSection = ({ order, onSave, isPending }: LegalReviewSectionProps) => {
  const legal = order.legal;
  const [open, setOpen] = useState(false);

  const flagCount = [
    legal?.restrictedContentFlag,
    legal?.copyrightConcernFlag,
    legal?.safetyConcernFlag,
  ].filter(Boolean).length;

  const summaryText = flagCount > 0 ? `${flagCount} flag${flagCount > 1 ? 's' : ''}` : 'No flags';

  const handleToggleFlag = (key: string, checked: boolean) => {
    onSave({
      legal: {
        ...(legal ?? {}),
        [key]: checked,
      },
    });
  };

  return (
    <div
      style={{
        border: '0.5px solid var(--border-default)',
        borderRadius: 'var(--radius-md)',
        marginBottom: '8px',
        overflow: 'hidden',
      }}
    >
      <div
        onClick={() => setOpen(!open)}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '10px 14px',
          cursor: 'pointer',
          fontSize: '13px',
          fontWeight: 'var(--font-weight-medium)',
          color: 'var(--text-primary)',
          transition: 'background-color 100ms',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--bg-surface-sunken)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
      >
        <span>Legal & content review</span>
        <span
          style={{
            fontSize: '11px',
            color: flagCount > 0 ? 'var(--color-error-600)' : 'var(--text-tertiary)',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          {summaryText}
          <span
            style={{
              transition: 'transform 200ms',
              transform: open ? 'rotate(90deg)' : 'rotate(0)',
              fontSize: '10px',
            }}
          >
            &#9654;
          </span>
        </span>
      </div>

      {open && (
        <div style={{ padding: '0 14px 10px', fontSize: '13px', color: 'var(--text-secondary)' }}>
          {legal?.termsAcceptedAt && (
            <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginBottom: '8px' }}>
              Terms accepted: {new Date(legal.termsAcceptedAt).toLocaleString()}
              {legal.fileOwnershipConfirmed && <> &middot; File ownership confirmed</>}
            </div>
          )}

          <FlagCheckbox
            label="Restricted content concern"
            checked={legal?.restrictedContentFlag ?? false}
            onChange={(checked) => handleToggleFlag('restrictedContentFlag', checked)}
            disabled={isPending}
          />
          <FlagCheckbox
            label="Copyright concern"
            checked={legal?.copyrightConcernFlag ?? false}
            onChange={(checked) => handleToggleFlag('copyrightConcernFlag', checked)}
            disabled={isPending}
          />
          <FlagCheckbox
            label="Safety concern"
            checked={legal?.safetyConcernFlag ?? false}
            onChange={(checked) => handleToggleFlag('safetyConcernFlag', checked)}
            disabled={isPending}
          />
        </div>
      )}
    </div>
  );
};

const FlagCheckbox = ({
  label,
  checked,
  onChange,
  disabled,
}: {
  readonly label: string;
  readonly checked: boolean;
  readonly onChange: (checked: boolean) => void;
  readonly disabled: boolean;
}) => (
  <label
    className="flex items-center"
    style={{ gap: '6px', fontSize: '13px', margin: '6px 0', cursor: disabled ? 'not-allowed' : 'pointer' }}
  >
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      disabled={disabled}
    />
    {label}
  </label>
);
